/**
 * Геометрия на шаре: расстояние и круг.
 *
 * Круг нужен потому, что нарисовать зону слоем MapLibre нельзя: у круглого
 * слоя радиус задаётся в пикселях, а зона живёт в метрах и обязана остаться
 * собой на любом увеличении. Поэтому круг строится многоугольником из точек,
 * каждая из которых отложена от центра ровно на радиус зоны.
 *
 * Считается по сферической формуле, а не плоской поправкой на косинус широты.
 * Плоская проще, но врёт тем сильнее, чем больше зона и чем севернее она
 * лежит, — а у нас предел радиуса сто километров и парк в Германии.
 */

/** Средний радиус Земли по МАС, метры. */
const EARTH_R = 6_371_008.8

const RAD = Math.PI / 180

/** Сколько вершин у круга. Шестьдесят четыре — на глаз уже гладкая дуга. */
const POINTS = 64

/** Расстояние между точками по большому кругу, метры. */
export function distanceM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = lat1 * RAD
  const φ2 = lat2 * RAD
  const dφ = (lat2 - lat1) * RAD
  const dλ = (lon2 - lon1) * RAD

  const a =
    Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2
  return 2 * EARTH_R * Math.asin(Math.min(1, Math.sqrt(a)))
}

/** Точка на расстоянии distance по азимуту bearing от заданной. */
function destination(
  lat: number,
  lon: number,
  bearing: number,
  distance: number,
): [number, number] {
  const δ = distance / EARTH_R
  const θ = bearing
  const φ1 = lat * RAD
  const λ1 = lon * RAD

  const φ2 = Math.asin(
    Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ),
  )
  const λ2 =
    λ1 +
    Math.atan2(
      Math.sin(θ) * Math.sin(δ) * Math.cos(φ1),
      Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2),
    )

  // GeoJSON ждёт пару «долгота, широта» — именно в этом порядке.
  return [λ2 / RAD, φ2 / RAD]
}

/**
 * Кольцо многоугольника, приближающего круг заданного радиуса.
 *
 * Возвращает пары «долгота, широта»; первая вершина повторена последней —
 * незамкнутое кольцо MapLibre рисует как обрывок дуги.
 */
export function circlePolygon(
  latitude: number,
  longitude: number,
  radiusM: number,
  points: number = POINTS,
): [number, number][] {
  const ring: [number, number][] = []
  for (let i = 0; i < points; i++) {
    ring.push(destination(latitude, longitude, (i / points) * 2 * Math.PI, radiusM))
  }
  ring.push(ring[0]!)
  return ring
}
